import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { db, auth, storage } from './firebase';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { QRCodeSVG } from 'qrcode.react';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import Compressor from 'compressorjs';
import './AdminPanel.css';

export default function AdminPanel({ onLogout }) {
  const [albums, setAlbums] = useState([]);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDate, setNewAlbumDate] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingAlbumId, setEditingAlbumId] = useState(null);

  // For managing guest uploads
  const [uploadsByAlbum, setUploadsByAlbum] = useState({});
  const [expandedAlbumId, setExpandedAlbumId] = useState(null);

  const formSectionRef = useRef(null);

  useEffect(() => {
    fetchAlbums();
  }, []);

  async function fetchAlbums() {
    const albumsRef = collection(db, 'albums');
    const snapshot = await getDocs(albumsRef);
    const albumsList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    albumsList.sort((a, b) => {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bTime - aTime;
    });

    setAlbums(albumsList);
  }

  const generateRandomCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCreateOrUpdateAlbum = async () => {
    if (!newAlbumName.trim()) {
      setMessage('Album name cannot be empty.');
      return;
    }
    if (!newAlbumDate) {
      setMessage('Please select a date.');
      return;
    }
    if (!bannerUrl.trim()) {
      setMessage('Please upload a banner image.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      if (editingAlbumId) {
        await updateDoc(doc(db, 'albums', editingAlbumId), {
          name: newAlbumName,
          date: newAlbumDate,
          bannerUrl: bannerUrl.trim(),
          updatedAt: serverTimestamp(),
        });
        setMessage('Album updated!');
      } else {
        const code = generateRandomCode();
        await addDoc(collection(db, 'albums'), {
          name: newAlbumName,
          code,
          date: newAlbumDate,
          bannerUrl: bannerUrl.trim(),
          createdAt: serverTimestamp(),
        });
        setMessage('Album created!');
      }

      setEditingAlbumId(null);
      setNewAlbumName('');
      setNewAlbumDate('');
      setBannerUrl('');
      await fetchAlbums();
    } catch (err) {
      setMessage('Error saving album: ' + err.message);
    }

    setLoading(false);
  };

  const handleDeleteAlbum = async (id) => {
    if (!window.confirm('Are you sure you want to delete this album?')) return;

    // Delete album banner and guest uploads also:
    try {
      // Find album by id
      const album = albums.find(a => a.id === id);
      if (!album) throw new Error('Album not found.');

      // Delete banner image if exists
      if (album.bannerUrl) {
        try {
          const bannerPath = album.bannerUrl.split('/o/')[1].split('?')[0]; // extract path
          const decodedPath = decodeURIComponent(bannerPath);
          await deleteObject(storageRef(storage, decodedPath));
        } catch {
          // ignore errors if banner path can't be deleted
        }
      }

      // Delete all guest photos for this album
      const q = query(collection(db, 'photos'), where('albumCode', '==', album.code));
      const snap = await getDocs(q);
      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        if (data.storagePath) {
          await deleteObject(storageRef(storage, data.storagePath));
        }
        await deleteDoc(doc(db, 'photos', docSnap.id));
      }

      // Delete album document
      await deleteDoc(doc(db, 'albums', id));
      setMessage('Album deleted.');
      await fetchAlbums();
      // Clear uploads cache if expanded album was deleted
      setUploadsByAlbum(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      if (expandedAlbumId === id) setExpandedAlbumId(null);
    } catch (err) {
      setMessage('Error deleting album: ' + err.message);
    }
  };

  const handleEditAlbum = (album) => {
    setEditingAlbumId(album.id);
    setNewAlbumName(album.name);
    setNewAlbumDate(album.date ? album.date.split('T')[0] : '');
    setBannerUrl(album.bannerUrl || '');
    setMessage('');

    formSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingAlbumId(null);
    setNewAlbumName('');
    setNewAlbumDate('');
    setBannerUrl('');
    setMessage('');
  };

  const handleLogout = async () => {
    await signOut(auth);
    if (onLogout) onLogout();
  };

  async function handleBannerFileChange(event) {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setMessage('');

    try {
      // Compress the image
      const compressedFile = await new Promise((resolve, reject) => {
        new Compressor(file, {
          quality: 0.6, // 60% quality for compression
          maxWidth: 1200, // Maximum width
          maxHeight: 1200, // Maximum height
          mimeType: 'image/jpeg', // Convert to JPEG
          success(result) {
            resolve(result);
          },
          error(err) {
            reject(err);
          },
        });
      });

      // Upload the compressed image
      const fileRef = storageRef(storage, `banners/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, compressedFile);
      const url = await getDownloadURL(fileRef);
      setBannerUrl(url);
      setMessage('Banner image uploaded!');
    } catch (error) {
      setMessage('Error uploading banner: ' + error.message);
    }

    setLoading(false);
  }

  // --- Guest uploads functions ---

  async function toggleUploads(album) {
    if (expandedAlbumId === album.id) {
      setExpandedAlbumId(null);
      return;
    }

    if (!uploadsByAlbum[album.id]) {
      const q = query(collection(db, 'photos'), where('albumCode', '==', album.code));
      const snap = await getDocs(q);
      const images = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUploadsByAlbum(prev => ({ ...prev, [album.id]: images }));
    }

    setExpandedAlbumId(album.id);
  }

  async function deleteUpload(albumId, image) {
    if (!window.confirm('Delete this image?')) return;

    try {
      await deleteDoc(doc(db, 'photos', image.id));
      if (image.storagePath) {
        const imageRef = storageRef(storage, image.storagePath);
        await deleteObject(imageRef);
      }

      setUploadsByAlbum(prev => ({
        ...prev,
        [albumId]: prev[albumId].filter(img => img.id !== image.id),
      }));
    } catch (err) {
      alert('Error deleting image: ' + err.message);
    }
  }

  const handleCloseModal = () => {
    setExpandedAlbumId(null);
  };

  return (
    <div className="container">
      <div className="header">
        <h1 className="brand">Formal Invites - Admin Panel</h1>
        <button onClick={handleLogout} className="logoutButton">Logout</button>
      </div>

      <section className="formSection" ref={formSectionRef}>
        <h2 className="title">{editingAlbumId ? 'Edit Album' : 'Create New Album'}</h2>
        <input
          type="text"
          placeholder="Album Name"
          value={newAlbumName}
          onChange={e => setNewAlbumName(e.target.value)}
          className="input"
        />
        <label className="dateLabel">Select Event Date</label>
        <input
          type="date"
          value={newAlbumDate}
          onChange={e => setNewAlbumDate(e.target.value)}
          className="input dateInput"
          required
        />

        <label className="fileUploadLabel">
          Upload Banner Image:
          <input
            type="file"
            accept="image/*"
            onChange={handleBannerFileChange}
            className="fileInput"
            disabled={loading}
          />
        </label>

        {bannerUrl && (
          <img
            src={bannerUrl}
            alt="Banner Preview"
            className="bannerPreview"
          />
        )}

        <div className="spacer"></div>

        <div className="buttonGroup">
          <button
            onClick={handleCreateOrUpdateAlbum}
            className="button"
            disabled={loading}
          >
            {loading
              ? (editingAlbumId ? 'Updating...' : 'Creating...')
              : (editingAlbumId ? 'Update Album' : 'Create Album')}
          </button>
          {editingAlbumId && (
            <button
              onClick={handleCancelEdit}
              className="button cancelButton"
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
        {message && <p className="message">{message}</p>}
      </section>

      <section className="listSection">
        <h2 className="title">Albums List</h2>
        {albums.length === 0 && <p>No albums created yet.</p>}

        {albums.map(album => (
          <div key={album.id} className="albumCard">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
              <div className="albumInfo">
                <h3 className="albumName">{album.name}</h3>
                <Link to={`/guest/${album.code}`} className="albumLink">
                  Go to Guest Page
                </Link>
                <p><strong>Code:</strong> {album.code}</p>
                {album.date && (
                  <p><strong>Date:</strong> {formatDateLocal(album.date)}</p>
                )}
                {album.bannerUrl && (
                  <img
                    src={album.bannerUrl}
                    alt="Event Banner"
                    className="bannerImage"
                  />
                )}
                <div style={{ marginTop: 10 }}>
                  <button
                    onClick={() => toggleUploads(album)}
                    className="editButton"
                  >
                    {expandedAlbumId === album.id ? 'Hide Uploads' : 'View Uploads'}
                  </button>
                </div>
              </div>

              <div className="qrContainer">
                <QRCodeSVG value={`${window.location.origin}/guest/${album.code}`} size={100} />
                <div className="qrButtons">
                  <button
                    onClick={() => handleEditAlbum(album)}
                    className="editButton"
                    title="Edit Album"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteAlbum(album.id)}
                    className="deleteButton"
                    title="Delete Album"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {expandedAlbumId === album.id && uploadsByAlbum[album.id] && (
              <div className="modal">
                <div className="modalContent">
                  <button
                    onClick={handleCloseModal}
                    className="modalCloseButton"
                  >
                    ×
                  </button>
                  <h3>{album.name} - Guest Uploads</h3>
                  {uploadsByAlbum[album.id].length === 0 ? (
                    <p>No uploads yet.</p>
                  ) : (
                    <div className="uploadsGallery">
                      {uploadsByAlbum[album.id].map(image => (
                        <div
                          key={image.id}
                          className="uploadThumbnail"
                          title={image.filename || 'Guest upload'}
                        >
                          <img
                            src={image.url}
                            alt=""
                          />
                          <button
                            onClick={() => deleteUpload(album.id, image)}
                            className="deleteButton"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const localDate = new Date(dateStr + 'T00:00:00');
  return localDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
